import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { useTranslation } from "../../context/LanguageContext";
import { useUserContext } from "../../context/UserContext";
import { toast } from "react-toastify";
import { isZipCode } from "./utils";

interface LanguageStepProps {
  zipCode: string;
  onZipCodeChange: (zipCode: string) => void;
}

export default function LanguageStep({
  zipCode,
  onZipCodeChange,
}: LanguageStepProps) {
  const { t } = useTranslation();
  const { user, updateUser } = useUserContext();
  const [isSavingZip, setIsSavingZip] = useState(false);
  const [lastSavedZip, setLastSavedZip] = useState("");

  // Initialize lastSavedZip with user's current zip code
  useEffect(() => {
    if (user?.zipCode) {
      setLastSavedZip(user.zipCode);
    }
  }, [user?.zipCode]);

  // Auto-save zip code when valid and different from last saved
  useEffect(() => {
    const saveZipCode = async () => {
      if (
        isZipCode(zipCode) &&
        zipCode !== lastSavedZip &&
        !isSavingZip &&
        user
      ) {
        setIsSavingZip(true);
        try {
          await updateUser({ zipCode: zipCode.trim() });
          setLastSavedZip(zipCode);
          toast.success("Zip code saved successfully");
        } catch (error) {
          console.error("Error updating zip code:", error);
          toast.error("Failed to save zip code");
        } finally {
          setIsSavingZip(false);
        }
      }
    };

    // Debounce the save by 500ms
    const timeoutId = setTimeout(() => {
      saveZipCode();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [zipCode, lastSavedZip, isSavingZip, user, updateUser]);

  return (
    <div className="max-w-md mx-auto space-y-8">
      {/* Zip Code Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-purple-50 dark:bg-purple-900/30 border border-purple-100 dark:border-purple-800 shadow-sm">
            <MapPin className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
              {t("setup.yourZipCode")}
            </h2>
            <p className="text-xs text-gray-600 dark:text-dark-400 mt-0.5">
              Find deals in your area
            </p>
          </div>
        </div>

        <div className="space-y-2.5">
          <input
            type="text"
            placeholder={t("setup.zipCodePlaceholder")}
            value={zipCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 5);
              onZipCodeChange(value);
            }}
            className={`w-full px-4 py-3.5 border rounded-2xl text-center text-lg font-mono font-bold focus:outline-none focus:ring-4 transition-all duration-300 ${
              zipCode.length === 0
                ? "bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-600 focus:ring-purple-500/20 focus:border-purple-400 dark:focus:border-purple-500 shadow-sm text-gray-900 dark:text-white"
                : isZipCode(zipCode)
                ? "bg-green-50 dark:bg-green-900/30 border-green-400 dark:border-green-600 focus:ring-green-500/20 text-green-900 dark:text-green-300 shadow-sm shadow-green-500/10"
                : "bg-red-50 dark:bg-red-900/30 border-red-400 dark:border-red-600 focus:ring-red-500/20 text-red-900 dark:text-red-300 shadow-sm shadow-red-500/10"
            }`}
            maxLength={5}
          />

          {zipCode.length > 0 && !isZipCode(zipCode) && (
            <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/30 px-3 py-2.5 rounded-xl border border-red-200 dark:border-red-800 shadow-sm">
              <span className="text-sm mt-0.5 flex-shrink-0">⚠️</span>
              <p className="text-xs font-medium text-red-900 dark:text-red-300 leading-relaxed">
                {t("setup.invalidZipCode")}
              </p>
            </div>
          )}

          {isZipCode(zipCode) && (
            <div className="flex items-start gap-2 bg-green-50 dark:bg-green-900/30 px-3 py-2.5 rounded-xl border border-green-200 dark:border-green-800 shadow-sm">
              {isSavingZip ? (
                <span className="inline-flex items-center mt-0.5 flex-shrink-0">
                  <div className="w-3 h-3 border-2 border-green-600 dark:border-green-400 border-t-transparent rounded-full animate-spin" />
                </span>
              ) : (
                <span className="text-sm mt-0.5 flex-shrink-0">✓</span>
              )}
              <p className="text-xs font-medium text-green-900 dark:text-green-300 leading-relaxed">
                {isSavingZip ? "Saving..." : t("setup.validZipCode")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
